import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    ArrowRight,
    BedDouble,
    BrushCleaning,
    Users,
    Wrench,
} from "lucide-react";

const WORKFLOWS = [
    { icon: BedDouble, name: "Rooms and reservations" },
    { icon: Users, name: "Guest records" },
    { icon: BrushCleaning, name: "Housekeeping" },
    { icon: Wrench, name: "Maintenance" },
];

export default function Landing() {
    return (
        <>
            <section className="relative" aria-labelledby="landing-title">
                <div className="absolute inset-0 kpi-hero" aria-hidden="true" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
                    <p className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground mb-5">
                        Early-stage prototype · Active development
                    </p>
                    <h1 id="landing-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">
                        Hotel operations in one workspace.
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
                        HotelOS is an early-stage, self-hosted hotel operations prototype in active development. Its current app brings core day-to-day workflows into one workspace.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <Button asChild size="lg" data-testid="landing-cta-contact">
                            <a href="https://zelkai.com/contact.html">
                                Enquire about HotelOS
                            </a>
                        </Button>
                        <Button asChild size="lg" variant="outline" data-testid="landing-cta-login">
                            <Link to="/login">
                                Owner sign in <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16" aria-labelledby="workflows-title">
                <div className="max-w-2xl mb-6">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Current prototype</p>
                    <h2 id="workflows-title" className="text-2xl sm:text-3xl font-semibold tracking-tight mt-2">
                        Available workflows
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2">
                        The app currently includes working areas for rooms, reservations, guest records, housekeeping and maintenance.
                    </p>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="landing-workflows">
                    {WORKFLOWS.map(({ icon: Icon, name }) => (
                        <li key={name} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                            <Icon className="w-5 h-5 text-[hsl(var(--primary))]" aria-hidden="true" />
                            <span className="font-semibold text-sm">{name}</span>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="border-y border-border bg-secondary/40" aria-labelledby="availability-title">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                    <h2 id="availability-title" className="text-xl font-semibold tracking-tight">
                        Access to the hosted instance
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                        Hosted sign-in is currently limited to the project owner. Public sign-up, guest booking and the AI concierge are not available on this instance.
                    </p>
                </div>
            </section>
        </>
    );
}
